/** @format */

import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ComponentRef,
  DestroyRef,
  ElementRef,
  PLATFORM_ID,
  TemplateRef,
  afterRenderEffect,
  computed,
  effect,
  inject,
  input,
  signal,
  untracked,
  viewChild,
  viewChildren,
} from '@angular/core';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  HmfButtonComponent,
  HmfModalComponent,
  HmfModalService,
  HmfSelectableGroupComponent,
  HmfSnackbarComponent,
} from '@dsg-tech/homefield-angular';
import { HmfMediaSetItem, HmfSelectableOption } from '@dsg-tech/homefield-utils';
import {
  HomefieldIconsLibraryService,
  homefieldIconChevronLeftOutline,
  homefieldIconChevronRightOutline,
  homefieldIconSignCancelOutline,
} from '@dsg-tech/homefield-angular-icons';

import { BreakPointNew } from '@shared/data-access/enums';
import { BreakpointsService } from '@shared/data-access/services';
import {
  ImageLoadingStatus,
  ImageLoadingStatusDirective,
  ScrollGradientXDirective,
} from '@shared/utils/directives';
import { MediaVideoComponent } from '@shared/media/components';
import { PlayButtonComponent } from '../play-button/play-button.component';
import { LoadingSpinnerComponent } from '../loading-spinner/loading-spinner.component';

/** Thumbnail metadata used by the selectable-group content template. */
interface GalleryThumbnailOption extends HmfSelectableOption {
  index: number;
  isVideo: boolean;
}

/** 2D translate offset (px) applied to the zoomed main image, relative to center. */
interface PanOffset {
  x: number;
  y: number;
}

/** Responsive image and video gallery with keyboard, swipe, and zoom support. */
@Component({
  selector: 'shared-component-image-gallery',
  templateUrl: './image-gallery.component.html',
  styleUrl: './image-gallery.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    HmfButtonComponent,
    HmfSnackbarComponent,
    HmfSelectableGroupComponent,
    ScrollGradientXDirective,
    MediaVideoComponent,
    PlayButtonComponent,
    ImageLoadingStatusDirective,
    LoadingSpinnerComponent,
  ],
})
export class ImageGalleryComponent {
  private static readonly IMAGE_UNAVAILABLE_URL =
    'https://images.dickssportinggoods.com/assets/icons/dsg/ImageUnavailablePDPSwatch.svg?qlt=70&wid=600&fmt=pjpeg';

  private readonly platformId = inject(PLATFORM_ID);
  private readonly document = inject(DOCUMENT);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  private readonly breakpointsService = inject(BreakpointsService);
  private readonly modalService = inject(HmfModalService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly homefieldIconsLibraryService = inject(HomefieldIconsLibraryService);

  // Homefield starts dismissDelay before entry completes and fixes its exit at 200ms.
  // Keep the interaction lock at 250ms even though the visual exit finishes sooner.
  private static readonly ZOOM_HINT_FADE_IN_MS = 200;
  private static readonly ZOOM_HINT_STATIC_HOLD_MS = 1000;
  private static readonly ZOOM_HINT_FADE_OUT_MS = 250;

  private static readonly SWIPE_THRESHOLD_PX = 50;

  // Caps rubber-band overscroll at the ends of the gallery.
  private static readonly RUBBER_BAND_EDGE_MAX_PULL_PX = 120;

  // Prevents a pathological drag from revealing unmounted slides.
  private static readonly MAX_DRAG_OFFSET_PX = 1024;

  static readonly ZOOM_LOCKOUT_MS =
    ImageGalleryComponent.ZOOM_HINT_FADE_IN_MS +
    ImageGalleryComponent.ZOOM_HINT_STATIC_HOLD_MS +
    ImageGalleryComponent.ZOOM_HINT_FADE_OUT_MS;

  // Shows the zoom hint once per product per browser session.
  private static readonly ZOOM_HINT_SESSION_STORAGE_PREFIX = 'pdp-gallery-zoom-hint-shown:';

  private static readonly DESKTOP_ZOOM_SCALE = 2;
  private static readonly MAX_PINCH_SCALE = 3;
  private static readonly DOUBLE_TAP_WINDOW_MS = 300;

  // Inputs
  readonly mediaItems = input<HmfMediaSetItem[]>([]);
  readonly productName = input<string>('');
  readonly swatchColor = input<string>('');
  // Falls back to productName when callers do not provide an eCode.
  readonly eCode = input<string>('');

  // Template refs
  private readonly zoomHintSnackbar = viewChild<HmfSnackbarComponent>('zoomHintSnackbar');
  private readonly zoomAreaRefs = viewChildren<ElementRef<HTMLElement>>('zoomAreaRef');
  private readonly mainImageRefs = viewChildren('mainImageRef', {
    read: ElementRef<HTMLElement>,
  });

  private readonly swipeAreaRef = viewChild<ElementRef<HTMLElement>>('swipeAreaRef');
  readonly modalContent = viewChild.required<TemplateRef<unknown>>('modalContent');
  private modalRef: ComponentRef<HmfModalComponent> | undefined;

  // State
  readonly isOpen = signal(false);
  readonly activeIndex = signal(0);
  readonly thumbnailsLoading = signal<Map<number, boolean>>(new Map());
  private readonly mainImageLoadingStatuses = signal<Map<string, ImageLoadingStatus>>(new Map());
  private readonly pendingImagePreloads = new Set<HTMLImageElement>();

  private readonly activeSlideElements = computed(() => {
    const activeSlideIndex = String(this.activeIndex());

    return {
      zoomArea: this.findElementBySlideIndex(this.zoomAreaRefs(), activeSlideIndex),
      mainImage: this.findElementBySlideIndex(this.mainImageRefs(), activeSlideIndex),
    };
  });

  private readonly activeZoomArea = computed(() => this.activeSlideElements().zoomArea);

  private readonly activeMainImage = computed(() => this.activeSlideElements().mainImage);

  // Plain field on purpose -- nothing reads it reactively (getPanBounds() runs from
  // pointer/touch handlers and the ResizeObserver callback, not a computed/effect/template).
  // Refreshed on slide change, zoom-area/picture resize, and image load, so the
  // pointermove/touchmove hot path never forces a layout read.
  private pictureContentSize: { width: number; height: number } | null = null;

  // Live touch offset and transition state for the swipe track.
  readonly dragOffsetPx = signal(0);
  readonly isDragging = signal(false);

  // Suppresses the transition for non-adjacent thumbnail jumps.
  readonly skipTrackTransition = signal(false);

  // Zoom applies only to image slides and resets on navigation.
  readonly zoomScale = signal(1);
  readonly panOffset = signal<PanOffset>({ x: 0, y: 0 });
  readonly isZoomed = computed<boolean>(() => this.zoomScale() > 1);

  readonly isSwiping = signal(false);

  // Blocks zoom while the helper snackbar is visible.
  readonly isZoomHintLocked = signal(false);
  readonly isZoomLocked = computed<boolean>(() => {
    const activeImage = this.activeImage();

    if (activeImage?.type !== 'image') return true;

    return this.isZoomHintLocked() || !this.isMainImageLoaded(activeImage);
  });

  readonly zoomAnnouncement = signal('');

  // Pointer capability stays independent from viewport layout.
  readonly isTouchDevice = signal(false);

  private pinchStartDistance = 0;
  private pinchStartScale = 1;
  private panStartTouch: { x: number; y: number } | null = null;
  private panStartOffset: PanOffset = { x: 0, y: 0 };
  private lastTouchEndTime = 0;
  private zoomLockoutTimer: ReturnType<typeof setTimeout> | undefined;
  private zoomTransformRafId: number | undefined;
  private pendingZoomTransform = 'none';
  private swipeStartX: number | null = null;
  private swipeStartY: number | null = null;

  readonly isMobile = computed<boolean>(
    () => this.breakpointsService.breakpoint() < BreakPointNew.MEDIUM,
  );

  readonly selectableSizeByBreakpoint = computed<string>(() => {
    const breakpoint = this.breakpointsService.breakpoint();

    if (breakpoint < BreakPointNew.MEDIUM) {
      return '56px';
    }

    if (breakpoint < BreakPointNew.LARGE) {
      return '72px';
    }

    return '80px';
  });

  // Mobile and tablet use the stacked layout; desktop uses side-by-side thumbnails.
  readonly useStackedLayout = computed<boolean>(
    () => this.breakpointsService.breakpoint() <= BreakPointNew.MEDIUM,
  );

  readonly mainImageSizes = computed<string>(
    () => '(max-width: 768px) 100vw, (max-width: 1023px) 80vw, 70vw',
  );

  readonly activeImage = computed<HmfMediaSetItem | null>(() => {
    const items = this.mediaItems();
    const index = this.activeIndex();

    if (!items || items.length === 0 || index < 0 || index >= items.length) {
      return null;
    }

    return items[index];
  });

  // Keep only the active slide and its immediate neighbors mounted.
  isSlideInWindow(slideIndex: number): boolean {
    return Math.abs(slideIndex - this.activeIndex()) <= 1;
  }

  readonly hasMultipleImages = computed<boolean>(() => {
    const items = this.mediaItems();
    return Boolean(items && items.length > 1);
  });

  readonly canGoPrevious = computed<boolean>(() => {
    const index = this.activeIndex();
    return index > 0;
  });

  readonly canGoNext = computed<boolean>(() => {
    const items = this.mediaItems();
    const index = this.activeIndex();
    return items && items.length > 0 && index < items.length - 1;
  });

  readonly imageCountLabel = computed<string>(() => {
    const items = this.mediaItems();
    const index = this.activeIndex();

    if (!items || items.length === 0) {
      return '0 of 0';
    }

    const current = index + 1;
    const total = items.length;
    return `${current} of ${total}`;
  });

  readonly isThumbnailLoading = computed<(index: number) => boolean>(() => {
    return (index: number): boolean => {
      if (index < 0) {
        return true;
      }

      const loadingMap = this.thumbnailsLoading();
      return loadingMap.get(index) ?? true;
    };
  });

  // Scene7 embeds the video URL in html; direct src values take precedence.
  readonly activeVideoUrl = computed<string | null>(() => {
    const image = this.activeImage();
    if (image?.type !== 'video') {
      return null;
    }

    if (image.src) {
      return image.src;
    }

    if (image.html) {
      const sourceMatch = /<source\b[^>]*\bsrc=["']([^"']+)["']/i.exec(image.html);
      return sourceMatch ? sourceMatch[1] : null;
    }

    return null;
  });

  // Non-active videos use posters to avoid mounting and buffering every player.
  protected posterUrlFor(item: HmfMediaSetItem): string {
    return item.poster || item.thumb || item.templateSrc || '';
  }

  protected handleMainImageError(event: Event): void {
    const image = event.target;

    if (
      !(image instanceof HTMLImageElement) ||
      image.src === ImageGalleryComponent.IMAGE_UNAVAILABLE_URL
    ) {
      return;
    }

    image.srcset = '';
    image.src = ImageGalleryComponent.IMAGE_UNAVAILABLE_URL;
  }

  protected mainImageSrc(item: HmfMediaSetItem | null | undefined): string {
    return item?.src || item?.templateSrc || '';
  }

  protected isMainImageDelayed(item: HmfMediaSetItem): boolean {
    return this.mainImageLoadingStatus(item) === 'delayed';
  }

  protected isMainImageLoaded(item: HmfMediaSetItem): boolean {
    return this.mainImageLoadingStatus(item) === 'loaded';
  }

  protected isMainImageLoading(item: HmfMediaSetItem): boolean {
    const status = this.mainImageLoadingStatus(item);
    return status !== 'loaded' && status !== 'error';
  }

  protected setMainImageLoadingStatus(item: HmfMediaSetItem, status: ImageLoadingStatus): void {
    const src = this.mainImageSrc(item);

    if (!src) return;

    this.mainImageLoadingStatuses.update((current) => {
      const updated = new Map(current);
      updated.set(src, status);
      return updated;
    });

    // Natural dimensions are only known once the image finishes loading.
    if (status === 'loaded') {
      this.refreshContentSize();
    }
  }

  private mainImageLoadingStatus(item: HmfMediaSetItem): ImageLoadingStatus | undefined {
    return this.mainImageLoadingStatuses().get(this.mainImageSrc(item));
  }

  // Main image zoom and pan transform.
  readonly zoomTransform = computed<string>(() => {
    const { x, y } = this.panOffset();
    return `translate(${x}px, ${y}px) scale(${this.zoomScale()})`;
  });

  // Mix the settled percentage position with the live pixel drag offset.
  readonly trackTransform = computed<string>(
    () => `translateX(calc(${-this.activeIndex() * 100}% + ${this.dragOffsetPx()}px))`,
  );

  readonly zoomAriaLabel = computed<string>(() => (this.isZoomed() ? 'Zoom out' : 'Zoom image'));

  readonly zoomCursorClass = computed<string>(() => {
    if (this.isTouchDevice() || this.isZoomLocked()) {
      return '';
    }
    return this.isZoomed() ? 'gallery-zoom-out-cursor' : 'gallery-zoom-in-cursor';
  });

  readonly zoomHintMessage = computed<string>(() =>
    this.isTouchDevice() ? 'Double-tap to Zoom' : 'Click to Zoom',
  );

  // Include the entry animation so the visible hold lasts the full duration.
  readonly zoomHintDismissDelaySeconds = computed<number>(
    () =>
      (ImageGalleryComponent.ZOOM_HINT_FADE_IN_MS +
        ImageGalleryComponent.ZOOM_HINT_STATIC_HOLD_MS) /
      1000,
  );

  readonly activeIndexValue = computed<string>(() => String(this.activeIndex()));

  readonly thumbnailGap = computed<string>(() => (this.useStackedLayout() ? '8px' : '16px'));

  readonly thumbnailOptions = computed<GalleryThumbnailOption[]>(() => {
    const items = this.mediaItems() ?? [];
    const name = this.productName();
    const total = items.length;

    return items.map((item, index) => {
      const isVideo = item?.type === 'video';
      // The option label names the asset type; image alt text omits the redundant "Image."
      const assetLabel = isVideo ? 'Video' : 'Image';

      return {
        id: String(index),
        value: String(index),
        label: `${name}, ${assetLabel} ${index + 1} of ${total}`,
        imageUrl: item?.thumb || item?.poster || item?.templateSrc || '',
        imageAlt: `${name}, ${index + 1} of ${total}`,
        index,
        isVideo,
      };
    });
  });

  constructor() {
    this.homefieldIconsLibraryService.registerIcons([
      homefieldIconSignCancelOutline,
      homefieldIconChevronLeftOutline,
      homefieldIconChevronRightOutline,
    ]);

    if (isPlatformBrowser(this.platformId)) {
      this.isTouchDevice.set(this.detectTouchDevice());
    }

    this.setupKeyboardNavigation();
    this.setupActiveThumbnailScrollIntoView();
    this.setupZoomHintForLoadedActiveImage();
    this.setupZoomResetOnNavigation();
    this.setupZoomTransformSync();
    this.setupZoomAreaNonPassiveTouchMove();
    this.setupZoomAreaResizeObserver();
    this.setupSwipeAreaNonPassiveTouchMove();
    this.setupImagePreloadReset();

    this.destroyRef.onDestroy(() => {
      this.clearZoomLockoutTimer();
      this.cancelZoomTransformRaf();
      this.pendingImagePreloads.clear();
      this.modalRef?.destroy();
      this.modalRef = undefined;
    });
  }

  open(index = 0): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const items = untracked(() => this.mediaItems());
    if (!items || items.length === 0) {
      return;
    }

    this.pauseActiveVideo();

    const clampedIndex = Math.max(0, Math.min(index, items.length - 1));
    this.activeIndex.set(clampedIndex);
    this.isOpen.set(true);
    this.preloadGalleryImages(clampedIndex);

    const loadingMap = new Map<number, boolean>();
    items.forEach((_, idx) => loadingMap.set(idx, true));
    this.thumbnailsLoading.set(loadingMap);

    if (!this.modalRef) {
      this.modalRef = this.modalService.createModal(
        {
          containerClassName: 'image-gallery-modal',
          title: '',
          closable: true,
          clickableBackground: true,
          showFooter: false,
          focus: true,
        },
        this.modalContent(),
      );

      this.modalRef.instance.closed
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => this.close());
    }

    this.modalRef.instance.openModal();
  }

  close(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    this.pauseActiveVideo({ rewind: false });

    if (!this.isOpen()) {
      return;
    }

    this.isOpen.set(false);

    // Avoid re-closing when the modal's closed event invokes this method.
    if (this.modalRef?.instance.open()) {
      this.modalRef.instance.closeModal();
    }

    this.clearZoomLockoutTimer();
    this.isZoomHintLocked.set(false);
    this.changeDetectorRef.markForCheck();
  }

  selectThumbnail(index: number): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const items = this.mediaItems();

    if (!items || items.length === 0 || index < 0 || index >= items.length) {
      return;
    }

    this.pauseActiveVideo();

    // Skip animation when a thumbnail jumps over intermediate slides.
    if (Math.abs(index - this.activeIndex()) > 1) {
      this.skipTrackTransition.set(true);
      requestAnimationFrame(() => this.skipTrackTransition.set(false));
    }

    this.activeIndex.set(index);
  }

  onThumbnailSelectionChange(value: string | string[]): void {
    const selectedValue = Array.isArray(value) ? value[0] : value;
    const index = Number(selectedValue);

    if (Number.isNaN(index)) {
      return;
    }

    this.selectThumbnail(index);
  }

  goToPrevious(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    if (!this.canGoPrevious()) {
      return;
    }

    this.pauseActiveVideo();

    this.activeIndex.update((current) => current - 1);
  }

  goToNext(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    if (!this.canGoNext()) {
      return;
    }

    this.pauseActiveVideo();

    this.activeIndex.update((current) => current + 1);
  }

  onThumbnailLoad(index: number): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const items = this.mediaItems();

    if (!items || index < 0 || index >= items.length) {
      return;
    }

    const loadingMap = new Map(this.thumbnailsLoading());
    loadingMap.set(index, false);
    this.thumbnailsLoading.set(loadingMap);
  }

  // Modal content lives under document.body, outside this component's host.
  private pauseActiveVideo({ rewind = true }: { rewind?: boolean } = {}): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const video = this.document.querySelector<HTMLVideoElement>('.image-gallery-modal video');
    if (!video) {
      return;
    }

    video.pause();

    if (rewind && video.readyState > 0) {
      video.currentTime = 0;
    }
  }

  // Listen on globalThis so arrow navigation works across the modal focus trap.
  private setupKeyboardNavigation(): void {
    effect((onCleanup) => {
      if (!isPlatformBrowser(this.platformId)) {
        return;
      }

      const open = this.isOpen();

      if (open) {
        const keydownHandler = (event: KeyboardEvent): void => {
          if (event.key === 'ArrowLeft') {
            event.preventDefault();
            this.goToPrevious();
          } else if (event.key === 'ArrowRight') {
            event.preventDefault();
            this.goToNext();
          }
        };

        globalThis.addEventListener('keydown', keydownHandler);

        onCleanup(() => {
          globalThis.removeEventListener('keydown', keydownHandler);
        });
      }
    });
  }

  // Scroll after rendering because modal content lives in a separate component tree.
  private setupActiveThumbnailScrollIntoView(): void {
    afterRenderEffect({
      read: () => {
        if (!isPlatformBrowser(this.platformId)) {
          return;
        }

        const open = this.isOpen();
        const index = this.activeIndex();

        if (!open) {
          return;
        }

        if (this.scrollActiveThumbnailIntoView(index)) {
          return;
        }

        requestAnimationFrame(() => {
          // The modal may still be mounting on the first render pass.
          if (this.isOpen() && this.activeIndex() === index) {
            this.scrollActiveThumbnailIntoView(index);
          }
        });
      },
    });
  }

  private setupZoomHintForLoadedActiveImage(): void {
    afterRenderEffect({
      mixedReadWrite: () => {
        const activeImage = this.activeImage();

        if (
          isPlatformBrowser(this.platformId) &&
          this.isOpen() &&
          activeImage &&
          this.isMainImageLoaded(activeImage)
        ) {
          this.maybeShowZoomHint();
        }
      },
    });
  }

  private scrollActiveThumbnailIntoView(index: number): boolean {
    const activeThumbnail = this.document.querySelector<HTMLElement>(
      `.image-gallery-modal [data-thumbnail-index="${index}"]`,
    );

    if (!activeThumbnail) {
      return false;
    }

    // Center horizontal thumbnails; native clamping handles the first and last items.
    const container = activeThumbnail.closest<HTMLElement>('.gallery-thumbnails-mobile');

    if (container) {
      const containerRect = container.getBoundingClientRect();
      const thumbnailRect = activeThumbnail.getBoundingClientRect();

      // Retry if the modal has not completed layout.
      if (containerRect.width === 0 || thumbnailRect.width === 0) {
        return false;
      }

      const targetScrollLeft =
        container.scrollLeft +
        (thumbnailRect.left - containerRect.left) -
        (containerRect.width - thumbnailRect.width) / 2;

      container.scrollTo({ left: targetScrollLeft, behavior: this.scrollBehavior() });
      return true;
    }

    if (typeof activeThumbnail.scrollIntoView === 'function') {
      activeThumbnail.scrollIntoView({
        block: 'nearest',
        inline: 'nearest',
        behavior: this.scrollBehavior(),
      });
    }

    return true;
  }

  private scrollBehavior(): ScrollBehavior {
    if (typeof globalThis.matchMedia === 'function') {
      return globalThis.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
    }

    return 'smooth';
  }

  // ==========================================================================
  // Zoom and Pan
  // ==========================================================================

  // Touch devices use double-tap and pinch to avoid synthetic click duplication.
  onZoomClick(): void {
    if (this.isTouchDevice() || this.isZoomLocked()) {
      return;
    }

    this.toggleDesktopZoom();
  }

  onZoomKeydown(event: KeyboardEvent): void {
    if (event.repeat) {
      return;
    }

    if (event.key !== 'Enter' && event.key !== ' ' && event.key !== 'Spacebar') {
      return;
    }

    event.preventDefault();

    if (this.isZoomLocked()) {
      return;
    }

    this.toggleDesktopZoom();
  }

  onZoomPointerMove(event: PointerEvent): void {
    if (this.isTouchDevice() || this.isZoomLocked() || !this.isZoomed()) {
      return;
    }

    const container = event.currentTarget as HTMLElement;
    const rect = container.getBoundingClientRect();

    if (rect.width === 0 || rect.height === 0) {
      return;
    }

    const ratioX = this.clampNumber((event.clientX - rect.left) / rect.width, 0, 1);
    const ratioY = this.clampNumber((event.clientY - rect.top) / rect.height, 0, 1);

    this.panOffset.set(
      this.panFromPointerRatio(ratioX, ratioY, this.zoomScale(), rect.width, rect.height),
    );
  }

  onZoomTouchStart(event: TouchEvent): void {
    if (this.isZoomLocked()) {
      return;
    }

    if (event.touches.length === 2) {
      this.pinchStartDistance = this.getTouchDistance(event.touches);
      this.pinchStartScale = this.zoomScale();
      this.isSwiping.set(true);
    } else if (event.touches.length === 1 && this.isZoomed()) {
      const touch = event.touches[0];
      this.panStartTouch = { x: touch.clientX, y: touch.clientY };
      this.panStartOffset = this.panOffset();
      this.isSwiping.set(true);
    }
  }

  onZoomTouchMove(event: TouchEvent): void {
    if (this.isZoomLocked()) {
      return;
    }

    const container = event.currentTarget as HTMLElement;
    const rect = container.getBoundingClientRect();

    if (rect.width === 0 || rect.height === 0) {
      return;
    }

    if (event.touches.length === 2 && this.pinchStartDistance > 0) {
      event.preventDefault();

      const distance = this.getTouchDistance(event.touches);
      const rawScale = this.pinchStartScale * (distance / this.pinchStartDistance);
      const clampedScale = this.clampNumber(rawScale, 1, ImageGalleryComponent.MAX_PINCH_SCALE);

      this.zoomScale.set(clampedScale);
      this.panOffset.update((offset) =>
        this.clampPanOffset(offset, clampedScale, rect.width, rect.height),
      );
    } else if (event.touches.length === 1 && this.panStartTouch && this.isZoomed()) {
      event.preventDefault();

      const touch = event.touches[0];
      const deltaX = touch.clientX - this.panStartTouch.x;
      const deltaY = touch.clientY - this.panStartTouch.y;
      const rawOffset = {
        x: this.panStartOffset.x + deltaX,
        y: this.panStartOffset.y + deltaY,
      };

      const clampedOffset = this.clampPanOffset(
        rawOffset,
        this.zoomScale(),
        rect.width,
        rect.height,
      );

      this.panOffset.set(clampedOffset);

      // Re-base at the clamped edge so reversing direction pans back
      // immediately instead of waiting out the distance dragged past the edge.
      if (clampedOffset.x !== rawOffset.x || clampedOffset.y !== rawOffset.y) {
        this.panStartTouch = { x: touch.clientX, y: touch.clientY };
        this.panStartOffset = clampedOffset;
      }
    }
  }

  onZoomTouchEnd(event: TouchEvent): void {
    this.pinchStartDistance = 0;
    this.panStartTouch = null;

    if (event.touches.length > 0 || this.isZoomLocked()) {
      return;
    }

    this.isSwiping.set(false);

    const now = Date.now();
    const isDoubleTap = now - this.lastTouchEndTime < ImageGalleryComponent.DOUBLE_TAP_WINDOW_MS;
    this.lastTouchEndTime = isDoubleTap ? 0 : now;

    if (!isDoubleTap) {
      return;
    }

    if (this.isZoomed()) {
      this.resetZoom();
      this.announceZoomState('Image zoom reset');
    } else {
      this.zoomScale.set(ImageGalleryComponent.DESKTOP_ZOOM_SCALE);
      this.panOffset.set({ x: 0, y: 0 });
      this.announceZoomState('Image zoomed in');
    }
  }

  private toggleDesktopZoom(): void {
    if (this.isZoomed()) {
      this.resetZoom();
      this.announceZoomState('Image zoom reset');
    } else {
      this.zoomScale.set(ImageGalleryComponent.DESKTOP_ZOOM_SCALE);
      this.panOffset.set({ x: 0, y: 0 });
      this.announceZoomState('Image zoomed in');
    }
  }

  private resetZoom(): void {
    this.zoomScale.set(1);
    this.panOffset.set({ x: 0, y: 0 });
    this.isSwiping.set(false);
  }

  private announceZoomState(message: string): void {
    this.zoomAnnouncement.set(message);
  }

  onSwipeStart(event: TouchEvent): void {
    if (event.touches.length !== 1) {
      this.clearSwipeStart();
      return;
    }

    this.swipeStartX = event.touches[0].clientX;
    this.swipeStartY = event.touches[0].clientY;
    this.isDragging.set(true);
    this.dragOffsetPx.set(0);
  }

  // Horizontal swipes move the track; vertical gestures remain available for page scrolling.
  onSwipeTouchMove(event: TouchEvent): void {
    if (!this.useStackedLayout() || this.isZoomed()) {
      return;
    }

    if (event.touches.length !== 1 || this.swipeStartX === null || this.swipeStartY === null) {
      return;
    }

    const currentX = event.touches[0].clientX;
    const currentY = event.touches[0].clientY;
    const deltaX = currentX - this.swipeStartX;
    const deltaY = currentY - this.swipeStartY;

    if (Math.abs(deltaX) <= Math.abs(deltaY)) {
      return;
    }

    event.preventDefault();
    this.dragOffsetPx.set(this.applyRubberBandDamping(deltaX));
  }

  onSwipeEnd(event: TouchEvent): void {
    if (!this.useStackedLayout() || this.isZoomed()) {
      this.clearSwipeStart();
      return;
    }

    if (
      event.changedTouches.length === 0 ||
      this.swipeStartX === null ||
      this.swipeStartY === null
    ) {
      this.clearSwipeStart();
      return;
    }

    const endX = event.changedTouches[0].clientX;
    const endY = event.changedTouches[0].clientY;
    const deltaX = endX - this.swipeStartX;
    const deltaY = endY - this.swipeStartY;

    if (Math.abs(deltaY) > Math.abs(deltaX)) {
      this.clearSwipeStart();
      return;
    }

    if (deltaX < -ImageGalleryComponent.SWIPE_THRESHOLD_PX && this.canGoNext()) {
      this.goToNext();
    } else if (deltaX > ImageGalleryComponent.SWIPE_THRESHOLD_PX && this.canGoPrevious()) {
      this.goToPrevious();
    }

    this.clearSwipeStart();
  }

  // touchcancel replaces touchend when the browser or OS interrupts a gesture.
  onSwipeCancel(): void {
    this.clearSwipeStart();
  }

  private clearSwipeStart(): void {
    this.swipeStartX = null;
    this.swipeStartY = null;
    this.dragOffsetPx.set(0);
    this.isDragging.set(false);
  }

  // Apply bounded resistance only when dragging past the first or last image.
  private applyRubberBandDamping(deltaX: number): number {
    const atFirstImage = this.activeIndex() === 0;
    const atLastImage = this.activeIndex() === this.mediaItems().length - 1;

    if (!((atFirstImage && deltaX > 0) || (atLastImage && deltaX < 0))) {
      return this.clampNumber(
        deltaX,
        -ImageGalleryComponent.MAX_DRAG_OFFSET_PX,
        ImageGalleryComponent.MAX_DRAG_OFFSET_PX,
      );
    }

    const sign = Math.sign(deltaX);
    const magnitude = Math.abs(deltaX);
    return (
      sign *
      ImageGalleryComponent.RUBBER_BAND_EDGE_MAX_PULL_PX *
      (1 - Math.exp(-magnitude / ImageGalleryComponent.RUBBER_BAND_EDGE_MAX_PULL_PX))
    );
  }

  // Map a pointer position to a center-relative pan offset.
  private panFromPointerRatio(
    ratioX: number,
    ratioY: number,
    scale: number,
    viewportWidth: number,
    viewportHeight: number,
  ): PanOffset {
    const { maxX, maxY } = this.getPanBounds(scale, viewportWidth, viewportHeight);

    return {
      x: maxX - ratioX * 2 * maxX,
      y: maxY - ratioY * 2 * maxY,
    };
  }

  private clampPanOffset(
    offset: PanOffset,
    scale: number,
    viewportWidth: number,
    viewportHeight: number,
  ): PanOffset {
    const { maxX, maxY } = this.getPanBounds(scale, viewportWidth, viewportHeight);

    return {
      x: this.clampNumber(offset.x, -maxX, maxX),
      y: this.clampNumber(offset.y, -maxY, maxY),
    };
  }

  // Pan bounds come from the rendered (contain-fit) image content, not the
  // zoom-area viewport -- picture.gallery-main-img is capped at 90% of the
  // container and its <img> uses object-fit: contain, so the visible content
  // is smaller than the viewport and letterboxed. This also assumes the scale
  // transform is centered on the viewport and the picture is centered within
  // the zoom area (transform-origin: center center + flex centering in the
  // SCSS) -- keep those in sync if either one ever changes.
  private getPanBounds(
    scale: number,
    viewportWidth: number,
    viewportHeight: number,
  ): { maxX: number; maxY: number } {
    const { width: contentWidth, height: contentHeight } =
      this.pictureContentSize ?? this.measureContentSize();

    return {
      maxX: Math.max(0, (contentWidth * scale - viewportWidth) / 2),
      maxY: Math.max(0, (contentHeight * scale - viewportHeight) / 2),
    };
  }

  // offsetWidth/Height, unlike getBoundingClientRect, ignore the current transform.
  private measureContentSize(): { width: number; height: number } {
    const picture = this.activeMainImage();
    const image = picture?.querySelector('img');

    const boxWidth = picture?.offsetWidth ?? 0;
    const boxHeight = picture?.offsetHeight ?? 0;
    const naturalWidth = image?.naturalWidth ?? 0;
    const naturalHeight = image?.naturalHeight ?? 0;

    if (naturalWidth > 0 && naturalHeight > 0 && boxWidth > 0 && boxHeight > 0) {
      const fit = Math.min(boxWidth / naturalWidth, boxHeight / naturalHeight);
      return { width: naturalWidth * fit, height: naturalHeight * fit };
    }

    return { width: boxWidth, height: boxHeight };
  }

  private refreshContentSize(): void {
    const image = this.activeMainImage()?.querySelector('img');
    const ready = !!image && image.naturalWidth > 0 && image.naturalHeight > 0;
    this.pictureContentSize = ready ? this.measureContentSize() : null;
  }

  private clampNumber(value: number, min: number, max: number): number {
    return Math.min(Math.max(value, min), max);
  }

  private getTouchDistance(touches: TouchList): number {
    const [first, second] = [touches[0], touches[1]];
    return Math.hypot(second.clientX - first.clientX, second.clientY - first.clientY);
  }

  // Detect input capability rather than inferring it from viewport width.
  private detectTouchDevice(): boolean {
    if (typeof globalThis.matchMedia === 'function') {
      return globalThis.matchMedia('(pointer: coarse)').matches;
    }

    return 'ontouchstart' in globalThis;
  }

  private setupZoomResetOnNavigation(): void {
    effect(() => {
      const open = this.isOpen();
      this.activeIndex();

      if (!open) {
        return;
      }

      this.resetZoom();
    });
  }

  private setupZoomTransformSync(): void {
    effect(() => {
      this.pendingZoomTransform = this.zoomTransform();
      const activeImage = this.activeMainImage();

      if (
        !isPlatformBrowser(this.platformId) ||
        !activeImage ||
        this.zoomTransformRafId !== undefined
      ) {
        return;
      }

      this.zoomTransformRafId = requestAnimationFrame(() => {
        this.zoomTransformRafId = undefined;
        const currentImageRefs = this.mainImageRefs();
        const currentActiveImage = this.activeMainImage();

        for (const imageRef of currentImageRefs) {
          imageRef.nativeElement.style.transform =
            imageRef.nativeElement === currentActiveImage ? this.pendingZoomTransform : 'none';
        }
      });
    });
  }

  private cancelZoomTransformRaf(): void {
    if (this.zoomTransformRafId !== undefined) {
      cancelAnimationFrame(this.zoomTransformRafId);
      this.zoomTransformRafId = undefined;
    }
  }

  private setupZoomAreaNonPassiveTouchMove(): void {
    effect((onCleanup) => {
      if (!isPlatformBrowser(this.platformId)) {
        return;
      }

      const zoomArea = this.activeZoomArea();

      if (!zoomArea) {
        return;
      }

      const handleTouchMove = (event: TouchEvent): void => this.onZoomTouchMove(event);

      zoomArea.addEventListener('touchmove', handleTouchMove, { passive: false });

      onCleanup(() => {
        zoomArea.removeEventListener('touchmove', handleTouchMove);
      });
    });
  }

  // Re-clamps pan on zoom-area or picture size changes (e.g. orientation change,
  // or a breakpoint swapping <picture> sources) so a previously valid offset
  // can't reveal whitespace at the new dimensions.
  private setupZoomAreaResizeObserver(): void {
    effect((onCleanup) => {
      if (!isPlatformBrowser(this.platformId) || typeof ResizeObserver === 'undefined') {
        return;
      }

      const zoomArea = this.activeZoomArea();
      const picture = this.activeMainImage();

      if (!zoomArea) {
        return;
      }

      this.refreshContentSize();

      const handleResize = (): void => {
        this.refreshContentSize();
        this.reclampPanOffsetForZoomArea(zoomArea);
      };

      const resizeObserver = new ResizeObserver(handleResize);
      resizeObserver.observe(zoomArea);

      if (picture) {
        resizeObserver.observe(picture);
      }

      onCleanup(() => {
        resizeObserver.disconnect();
      });
    });
  }

  private reclampPanOffsetForZoomArea(zoomArea: HTMLElement): void {
    if (!this.isZoomed()) {
      return;
    }

    const rect = zoomArea.getBoundingClientRect();
    this.panOffset.update((offset) =>
      this.clampPanOffset(offset, this.zoomScale(), rect.width, rect.height),
    );
  }

  private findElementBySlideIndex(
    elementRefs: readonly ElementRef<HTMLElement>[],
    slideIndex: string,
  ): HTMLElement | undefined {
    return elementRefs.find(({ nativeElement }) => nativeElement.dataset.slideIndex === slideIndex)
      ?.nativeElement;
  }

  private setupSwipeAreaNonPassiveTouchMove(): void {
    effect((onCleanup) => {
      if (!isPlatformBrowser(this.platformId)) {
        return;
      }

      const swipeArea = this.swipeAreaRef()?.nativeElement;

      if (!swipeArea) {
        return;
      }

      const handleTouchMove = (event: TouchEvent): void => this.onSwipeTouchMove(event);

      swipeArea.addEventListener('touchmove', handleTouchMove, { passive: false });

      onCleanup(() => {
        swipeArea.removeEventListener('touchmove', handleTouchMove);
      });
    });
  }

  private setupImagePreloadReset(): void {
    effect(() => {
      const items = this.mediaItems();

      untracked(() => {
        this.pendingImagePreloads.clear();

        if (this.isOpen()) {
          const clampedIndex = Math.max(0, Math.min(this.activeIndex(), items.length - 1));
          this.activeIndex.set(clampedIndex);
          this.preloadGalleryImages(clampedIndex);
        }
      });
    });
  }

  private preloadGalleryImages(activeIndex: number): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const items = this.mediaItems();
    const preloadOrder = [
      activeIndex,
      activeIndex + 1,
      activeIndex - 1,
      ...items.map((_, index) => index).filter((index) => Math.abs(index - activeIndex) > 1),
    ];

    for (const index of preloadOrder) {
      this.preloadImage(items[index], index === activeIndex ? 'high' : 'auto');
    }
  }

  private preloadImage(item: HmfMediaSetItem | undefined, fetchPriority: 'high' | 'auto'): void {
    if (!isPlatformBrowser(this.platformId) || !item || item.type === 'video') {
      return;
    }

    const src = this.mainImageSrc(item);
    if (!src) {
      return;
    }

    // Detached images warm the browser cache without adding rendered DOM.
    const image = new Image();
    image.decoding = 'async';
    image.loading = 'eager';
    image.fetchPriority = fetchPriority;
    image.sizes = this.mainImageSizes();
    image.srcset = item.srcset || '';

    this.pendingImagePreloads.add(image);
    image.src = src;
  }

  private maybeShowZoomHint(): void {
    const activeImage = this.activeImage();

    if (!activeImage || activeImage.type === 'video') {
      this.isZoomHintLocked.set(false);
      return;
    }

    if (this.hasShownZoomHintForProduct()) {
      return;
    }

    this.markZoomHintShownForProduct();
    this.isZoomHintLocked.set(true);
    this.getZoomHintSnackbar()?.openSnackbar();

    this.clearZoomLockoutTimer();
    this.zoomLockoutTimer = setTimeout(() => {
      this.isZoomHintLocked.set(false);
      // Explicitly close from our own timer so visibility is not coupled only
      // to the library's internal auto-dismiss behavior.
      this.getZoomHintSnackbar()?.closeSnackbar();
    }, ImageGalleryComponent.ZOOM_LOCKOUT_MS);
  }

  // Wrapper around the signal-based viewChild accessor so tests can stub this
  // method safely without redefining the Angular-managed property descriptor.
  private getZoomHintSnackbar(): HmfSnackbarComponent | undefined {
    return this.zoomHintSnackbar();
  }

  private clearZoomLockoutTimer(): void {
    if (this.zoomLockoutTimer !== undefined) {
      clearTimeout(this.zoomLockoutTimer);
      this.zoomLockoutTimer = undefined;
    }
  }

  /** Session-storage key scoping the frequency cap to the current product. */
  private zoomHintSessionKey(): string {
    const productKey = this.eCode() || this.productName() || 'unknown-product';
    return `${ImageGalleryComponent.ZOOM_HINT_SESSION_STORAGE_PREFIX}${productKey}`;
  }

  private hasShownZoomHintForProduct(): boolean {
    try {
      return globalThis.sessionStorage?.getItem(this.zoomHintSessionKey()) === 'true';
    } catch {
      // Private browsing or browser policy may disable session storage.
      return false;
    }
  }

  private markZoomHintShownForProduct(): void {
    try {
      globalThis.sessionStorage?.setItem(this.zoomHintSessionKey(), 'true');
    } catch {
      // The hint does not require storage to function.
    }
  }
}
